import {
    Autocomplete,
    Box,
    Button,
    Card,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    MenuItem,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import HelpOutline from '@mui/icons-material/HelpOutline';
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { LoadingContent } from '../../../components/Loading';
import ListPageLayout from '../../../components/ListPageLayout';
import { useFetch } from '../../../hooks/useFecth';
import { useSearchTextInput } from '../../../hooks/useSearchTextInput';
import SelectClub from '../../../components/SelectClub';
import BrokerSalesDialog from './BrokerSalesDialog';
import VerificationJobsDialog from './VerificationJobsDialog';

const statusOptions = [
    { value: '', label: 'Todos' },
    { value: 'WAITING', label: 'Aguardando' },
    { value: 'PROCESSING', label: 'Processando' },
    { value: 'PENDING', label: 'Pendente no broker' },
    { value: 'FREE', label: 'Liberado' },
    { value: 'VALIDATED', label: 'Validado' },
    { value: 'CONCLUDED', label: 'Concluído' },
    { value: 'REJECTED', label: 'Recusado no broker' },
    { value: 'CANCELED', label: 'Cancelado' },
];

const chipColorByStatus = status => {
    switch (status) {
        case 'FREE':
        case 'VALIDATED':
        case 'CONCLUDED':
            return 'success';
        case 'PROCESSING':
        case 'PENDING':
            return 'warning';
        case 'CANCELED':
        case 'REJECTED':
            return 'error';
        default:
            return 'default';
    }
};

const statusLabel = status =>
    statusOptions.find(option => option.value === status)?.label || status || 'Indefinido';

const formatCurrency = value =>
    Number(value || 0).toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });

const formatDateTime = date => {
    if (!date) return '—';
    const parsed = new Date(date);
    if (Number.isNaN(parsed.getTime())) return '—';
    return parsed.toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
};

function TwoLines({ primary, secondary, sx }) {
    return (
        <Stack spacing={0.25} sx={{ minWidth: 0, ...sx }}>
            <Typography variant="body2" fontWeight={600} noWrap title={primary}>
                {primary}
            </Typography>
            <Typography variant="body2" color="text.secondary" noWrap title={secondary}>
                {secondary}
            </Typography>
        </Stack>
    );
}

function rescueOfferTypes(rescue) {
    const source = rescue || {};
    const snapshot = source.offer || {};
    const types = [];
    if (source.isCashback || snapshot.isCashback) types.push({ key: 'cashback', label: 'Cashback' });
    if (source.isCoupon || snapshot.isCoupon) types.push({ key: 'coupon', label: 'Cupom' });
    if (source.isVoucher || snapshot.isVoucher) types.push({ key: 'voucher', label: 'Voucher' });
    return types;
}

function brokerLabel(rescue) {
    const raw = rescue?.broker || rescue?.offer?.offerChannel || rescue?.offer?.broker || '';
    const key = String(raw).toLowerCase();
    if (key === 'awin') return { key: 'awin', label: 'Awin' };
    if (key === 'lomadee' || key === 'socialsoul' || key === 'social-soul') {
        return { key: 'lomadee', label: 'Lomadee' };
    }
    return null;
}

function TypeChips({ types, broker }) {
    return (
        <Stack direction="row" gap={0.5} flexWrap="wrap">
            {types.length ? (
                types.map(type => (
                    <Chip key={type.key} size="small" label={type.label} variant="outlined" />
                ))
            ) : (
                <Chip size="small" label="Sem tipo" color="warning" variant="outlined" />
            )}
            {broker ? (
                <Chip
                    size="small"
                    label={broker.label}
                    color={broker.key === 'awin' ? 'secondary' : 'info'}
                    variant="outlined"
                />
            ) : null}
        </Stack>
    );
}

function HelpSection({ title, children }) {
    return (
        <Box>
            <Typography fontWeight={700} sx={{ mb: 0.5 }}>
                {title}
            </Typography>
            <Typography variant="body2" color="text.secondary" component="div">
                {children}
            </Typography>
        </Box>
    );
}

function RescuesHelpDialog({ open, onClose }) {
    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>Como funciona esta tela</DialogTitle>
            <DialogContent dividers>
                <Stack spacing={2.5}>
                    <HelpSection title="O que você está vendo">
                        Cada item é um resgate feito no app: a pessoa, o clube, a oferta e o anunciante.
                        Se a oferta veio da Lomadee ou da Awin, o chip do parceiro aparece ao lado do tipo.
                    </HelpSection>
                    <HelpSection title="Como a venda é encontrada">
                        Quando alguém resgata uma oferta desses parceiros, o app manda um código no link da loja.
                        Depois, o sistema procura na Lomadee e na Awin se nasceu uma compra com esse mesmo código.
                    </HelpSection>
                    <HelpSection title="Lomadee">
                        A cada meia hora o sistema pergunta à Lomadee se houve venda nos últimos 7 dias
                        com o código do resgate. Não chega aviso na hora: a confirmação espera essa consulta.
                    </HelpSection>
                    <HelpSection title="Awin">
                        Também consulta a cada meia hora. Além disso, a Awin avisa na hora quando a venda muda.
                        Os dois caminhos fazem a mesma coisa: atualizar o resgate quando a compra aparece.
                    </HelpSection>
                    <HelpSection title="Cashback e cupom">
                        Entram cashback e cupom (e voucher, se tiver o link da loja) dos últimos 4 meses,
                        de oferta Lomadee ou Awin. O app já manda o código no link do cupom dessas lojas.
                        Quando a compra aparece, o sistema guarda quanto a pessoa gastou e quanto economizou
                        no extrato. Só o cashback vai para o saldo de saque — desconto de cupom não vira saldo.
                    </HelpSection>
                    <HelpSection title="O que o status quer dizer">
                        <strong>Aguardando</strong> — a pessoa acabou de resgatar, ainda sem venda.
                        <br />
                        <strong>Pendente no broker</strong> — a loja registrou a compra, mas ainda não confirmou.
                        <br />
                        <strong>Validado</strong> — a compra foi aprovada. No cashback, o valor entra no saldo.
                        No cupom percentual, o extrato passa a mostrar o valor da compra e o desconto em reais.
                        <br />
                        <strong>Recusado no broker</strong> — a loja cancelou ou recusou a venda. Nada entra no saldo
                        nem no extrato como economia.
                    </HelpSection>
                    <HelpSection title="Quando não atualiza sozinho">
                        Compra sem o código do app, oferta que não é desses parceiros
                        ou resgate com mais de 4 meses ficam como estão.
                    </HelpSection>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Entendi</Button>
            </DialogActions>
        </Dialog>
    );
}

function RescueRow({ rescue, viewMode }) {
    const personName = rescue.person?.name || 'Não informado';
    const advertiserName = rescue.advertiser?.name || rescue.advertiser?.businessName || '—';
    const offerName = rescue.offer?.name || '—';
    const clubName = rescue.club?.name || '—';
    const types = rescueOfferTypes(rescue);
    const broker = brokerLabel(rescue);
    const statusChip = (
        <Chip
            size="small"
            label={statusLabel(rescue.status)}
            color={chipColorByStatus(rescue.status)}
            variant="outlined"
        />
    );

    if (viewMode === 'cards') {
        return (
            <Card variant="outlined" sx={{ p: 2, height: '100%' }}>
                <Stack spacing={1.25}>
                    <TypeChips types={types} broker={broker} />
                    <TwoLines primary={offerName} secondary={advertiserName} />
                    <TwoLines primary={personName} secondary={clubName} />
                    <TwoLines primary={formatCurrency(rescue.value)} secondary={formatDateTime(rescue.createdAt)} />
                    <Box>{statusChip}</Box>
                </Stack>
            </Card>
        );
    }

    return (
        <Card variant="outlined">
            <Stack
                direction={{ xs: 'column', md: 'row' }}
                alignItems={{ xs: 'stretch', md: 'center' }}
                spacing={{ xs: 1.5, md: 2 }}
                px={{ xs: 2, md: 2.5 }}
                py={1.75}
            >
                <Box sx={{ flex: '0 0 160px' }}>
                    <TypeChips types={types} broker={broker} />
                </Box>
                <TwoLines primary={offerName} secondary={advertiserName} sx={{ flex: 1.4 }} />
                <TwoLines primary={personName} secondary={clubName} sx={{ flex: 1.2 }} />
                <TwoLines
                    primary={formatCurrency(rescue.value)}
                    secondary={formatDateTime(rescue.createdAt)}
                    sx={{ flex: '0 0 150px' }}
                />
                <Box sx={{ alignSelf: { xs: 'flex-start', md: 'center' }, minWidth: 120 }}>
                    {statusChip}
                </Box>
            </Stack>
        </Card>
    );
}

export default function Rescues() {
    const [searchParams, setSearchParams] = useSearchParams();
    const { search, input, handleChange: handleSearchChange, handleReset: handleSearchReset } = useSearchTextInput(
        searchParams.get('search') || '',
    );

    const [statusValue, setStatusValue] = useState(searchParams.get('status') || '');
    const [clubId, setClubId] = useState(searchParams.get('clubId') || null);
    const [advertiserId, setAdvertiserId] = useState(searchParams.get('advertiserId') || null);
    const [fromDate, setFromDate] = useState(searchParams.get('from') || '');
    const [toDate, setToDate] = useState(searchParams.get('to') || '');
    const [helpOpen, setHelpOpen] = useState(false);
    const [awinSalesOpen, setAwinSalesOpen] = useState(false);
    const [lomadeeSalesOpen, setLomadeeSalesOpen] = useState(false);
    const [verificationOpen, setVerificationOpen] = useState(false);

    const params = useMemo(
        () => ({ ...Object.fromEntries(searchParams.entries()), ...(search ? { search } : {}) }),
        [searchParams, search],
    );

    const { data, isFetching } = useFetch('rescues', { params });
    const { data: advertisersList } = useFetch('advertiser', { params: { paged: false } });

    const rows = data?.rows ?? [];

    const advertiserOptions = useMemo(
        () =>
            (advertisersList || []).map(({ id, name, businessName }) => ({
                id,
                label: name || businessName || `Convênio ${id}`,
            })),
        [advertisersList],
    );
    const selectedAdvertiser = advertiserOptions.find(a => String(a.id) === String(advertiserId)) || null;

    const hasFilters = Array.from(searchParams.entries()).some(
        ([key, value]) => value !== '' && value !== null && key !== 'page' && key !== 'per_page',
    );

    const pageSize = Number(searchParams.get('per_page')) || 10;

    const applyFilters = close => {
        const nextParams = new URLSearchParams();
        if (search) nextParams.set('search', search);
        if (statusValue) nextParams.set('status', statusValue);
        if (clubId) nextParams.set('clubId', clubId);
        if (advertiserId) nextParams.set('advertiserId', advertiserId);
        if (fromDate) nextParams.set('from', fromDate);
        if (toDate) nextParams.set('to', toDate);
        if (pageSize !== 10) nextParams.set('per_page', String(pageSize));
        setSearchParams(nextParams);
        close?.();
    };

    const handleClearFilters = () => {
        setSearchParams({});
        handleSearchReset();
        setStatusValue('');
        setClubId(null);
        setAdvertiserId(null);
        setFromDate('');
        setToDate('');
    };

    const handlePageChange = (_, page) => {
        const nextParams = new URLSearchParams(searchParams);
        nextParams.set('page', page);
        setSearchParams(nextParams);
    };

    const handlePageSizeChange = nextSize => {
        const nextParams = new URLSearchParams(searchParams);
        nextParams.set('per_page', String(nextSize));
        nextParams.set('page', '1');
        setSearchParams(nextParams);
    };

    return (
        <>
        <ListPageLayout
            title="Resgates"
            subtitle="Acompanhe os resgates dos assinantes."
            counts={[{ label: 'resgates', value: data?.numRows ?? 0, color: 'primary' }]}
            loading={isFetching && !data}
            filterActive={hasFilters}
            onClearFilters={hasFilters ? handleClearFilters : null}
            viewStorageKey="admin-rescues-view"
            actions={
                <Stack direction="row" spacing={1} alignItems="center">
                    <Button size="small" variant="outlined" onClick={() => setAwinSalesOpen(true)}>
                        Vendas Awin
                    </Button>
                    <Button size="small" variant="outlined" onClick={() => setLomadeeSalesOpen(true)}>
                        Vendas Lomadee
                    </Button>
                    <Button size="small" variant="outlined" onClick={() => setVerificationOpen(true)}>
                        Verificações
                    </Button>
                    <Tooltip title="Como funciona esta tela">
                        <IconButton size="small" aria-label="Ajuda" onClick={() => setHelpOpen(true)}>
                            <HelpOutline />
                        </IconButton>
                    </Tooltip>
                </Stack>
            }
            isEmpty={!isFetching && rows.length === 0}
            empty={
                <Stack alignItems="center" spacing={1}>
                    <Typography variant="body1">Nenhum resgate encontrado.</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Ajuste os filtros ou tente novamente mais tarde.
                    </Typography>
                </Stack>
            }
            pagination={{
                page: data?.page || 1,
                pages: data?.pages || 1,
                total: data?.numRows ?? 0,
                pageSize,
                onChange: handlePageChange,
                onPageSizeChange: handlePageSizeChange,
                itemLabel: (data?.numRows ?? 0) === 1 ? 'resgate' : 'resgates',
            }}
            filterPanel={({ close }) => (
                <Stack spacing={2} component="form" onSubmit={e => { e.preventDefault(); applyFilters(close); }}>
                    <TextField
                        fullWidth
                        size="small"
                        label="Buscar"
                        placeholder="Nome, código, documento…"
                        value={input}
                        onChange={e => handleSearchChange(e.target.value)}
                    />
                    <TextField
                        select
                        fullWidth
                        size="small"
                        label="Status"
                        value={statusValue}
                        onChange={e => setStatusValue(e.target.value)}
                    >
                        {statusOptions.map(option => (
                            <MenuItem key={option.value || 'all'} value={option.value}>
                                {option.label}
                            </MenuItem>
                        ))}
                    </TextField>
                    <SelectClub
                        fullWidth
                        size="small"
                        value={clubId}
                        name="clubId"
                        onChange={e => setClubId(e.target.value || null)}
                    />
                    <Autocomplete
                        size="small"
                        options={advertiserOptions}
                        value={selectedAdvertiser}
                        onChange={(_, newValue) => setAdvertiserId(newValue?.id ?? null)}
                        getOptionLabel={opt => opt?.label ?? ''}
                        isOptionEqualToValue={(opt, val) => opt.id === val.id}
                        renderInput={p => <TextField {...p} label="Anunciante" />}
                    />
                    <Stack direction="row" spacing={1}>
                        <TextField
                            fullWidth
                            size="small"
                            label="De"
                            type="date"
                            value={fromDate}
                            onChange={e => setFromDate(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                        />
                        <TextField
                            fullWidth
                            size="small"
                            label="Até"
                            type="date"
                            value={toDate}
                            onChange={e => setToDate(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Stack>
                    <Button type="submit" variant="contained" fullWidth>
                        Aplicar filtros
                    </Button>
                </Stack>
            )}
        >
            {({ viewMode }) =>
                !data && isFetching ? (
                    <LoadingContent />
                ) : (
                    <Box
                        sx={{
                            p: 2,
                            display: viewMode === 'cards' ? 'grid' : 'flex',
                            flexDirection: 'column',
                            gap: 1.5,
                            gridTemplateColumns:
                                viewMode === 'cards'
                                    ? { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }
                                    : undefined,
                        }}
                    >
                        {rows.map(rescue => (
                            <RescueRow key={rescue.id} rescue={rescue} viewMode={viewMode} />
                        ))}
                    </Box>
                )
            }
        </ListPageLayout>
            <RescuesHelpDialog open={helpOpen} onClose={() => setHelpOpen(false)} />
            <BrokerSalesDialog open={awinSalesOpen} onClose={() => setAwinSalesOpen(false)} broker="awin" />
            <BrokerSalesDialog open={lomadeeSalesOpen} onClose={() => setLomadeeSalesOpen(false)} broker="lomadee" />
            <VerificationJobsDialog open={verificationOpen} onClose={() => setVerificationOpen(false)} />
    </>
    );
}
